import React from 'react'

import {Play_Filled_Corner0_Rounded as PlayIcon} from '#/components/icons/Play'
import {SpeakerVolumeFull_Stroke2_Corner0_Rounded as SpeakerIcon} from '#/components/icons/Speaker'
import {Text} from '#/components/Typography'

let HlsLib: any
async function ensureHls() {
  if (!HlsLib) {
    const mod = await import('hls.js')
    HlsLib = mod.default || mod
  }
}

type Props = {
  src: string
  poster?: string
  className?: string
  onPlayingChange?: (playing: boolean) => void
  active?: boolean
  index?: number
  activeIndex?: number
  soundEnabled?: boolean
  soundPrefSet?: boolean
  hasUserGesture?: boolean
  onSoundChange?: (enabled: boolean, fromUserGesture: boolean) => void
  onUserGesture?: () => void
}

const isDev =
  typeof __DEV__ !== 'undefined'
    ? __DEV__
    : process.env.NODE_ENV !== 'production'
const isClipsDebug = () =>
  isDev &&
  typeof globalThis !== 'undefined' &&
  Boolean((globalThis as any).__CLIPS_DEBUG__)

export default function VideoReel({
  src,
  poster,
  className,
  onPlayingChange,
  active = true,
  index,
  activeIndex: _activeIndex,
  soundEnabled: soundEnabledProp,
  soundPrefSet: soundPrefSetProp,
  hasUserGesture: hasUserGestureProp,
  onSoundChange,
  onUserGesture,
}: Props) {
  const ref = React.useRef<HTMLVideoElement | null>(null)
  const hlsRef = React.useRef<any>(null)
  const activeRef = React.useRef(active)
  const playAttemptRef = React.useRef(0)
  const [reloadKey, setReloadKey] = React.useState(0)
  const [fitClass, setFitClass] = React.useState<'fitContain' | 'fitCover'>(
    'fitContain',
  )
  const [needsUserGesture, setNeedsUserGesture] = React.useState(false)
  const [playbackError, setPlaybackError] = React.useState<string | null>(null)
  const [isBuffering, setIsBuffering] = React.useState(false)
  const [isPlaying, setIsPlaying] = React.useState(false)
  const [localSoundEnabled, setLocalSoundEnabled] = React.useState(false)
  const [localSoundPrefSet, setLocalSoundPrefSet] = React.useState(false)
  const [localHasUserGesture, setLocalHasUserGesture] = React.useState(false)
  const soundEnabled = soundEnabledProp ?? localSoundEnabled
  const soundPrefSet = soundPrefSetProp ?? localSoundPrefSet
  const hasUserGesture = hasUserGestureProp ?? localHasUserGesture
  const soundEnabledRef = React.useRef(soundEnabled)
  const hasUserGestureRef = React.useRef(hasUserGesture)
  const isHls = src.endsWith('.m3u8')

  React.useEffect(() => {
    activeRef.current = active
  }, [active])

  React.useEffect(() => {
    soundEnabledRef.current = soundEnabled
    hasUserGestureRef.current = hasUserGesture
  }, [soundEnabled, hasUserGesture])

  React.useEffect(() => {
    setPlaybackError(null)
    setNeedsUserGesture(false)
    setIsBuffering(false)
    setIsPlaying(false)
  }, [src])

  React.useEffect(() => {
    if (!active) return
    const video = ref.current
    if (!video) return
    let cancelled = false

    async function attach() {
      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }
      video.pause()
      video.muted = true
      // Keep inline playback enabled for iOS/Safari before attempting autoplay.
      ;(video as any).playsInline = true
      video.setAttribute('playsinline', 'true')
      video.setAttribute('webkit-playsinline', 'true')
      video.removeAttribute('src')
      video.load()

      if (isHls) {
        await ensureHls()
        if (cancelled) return
        const ua =
          typeof navigator !== 'undefined' ? navigator.userAgent || '' : ''
        const isSafari =
          /^((?!chrome|android).)*safari/i.test(ua) &&
          !/CriOS/i.test(ua) &&
          !/FxiOS/i.test(ua)
        const isIOSWebKit =
          /iP(ad|hone|od)/i.test(ua) && !/CriOS|FxiOS/i.test(ua)
        const canUseNativeHls = isSafari || isIOSWebKit
        if (canUseNativeHls) {
          video.src = src
          video.load()
        } else if (HlsLib?.isSupported()) {
          const hls = new HlsLib({maxBufferLength: 20, backBufferLength: 30})
          hlsRef.current = hls
          if (isClipsDebug()) {
            hls.on(HlsLib.Events.MANIFEST_PARSED, (_event: any, data: any) => {
              console.debug('[Clips][HLS] manifest parsed', {
                levels: data?.levels?.length ?? 0,
              })
            })
            hls.on(HlsLib.Events.ERROR, (_event: any, data: any) => {
              console.debug('[Clips][HLS] error', {
                fatal: data?.fatal,
                type: data?.type,
                details: data?.details,
              })
            })
          }
          hls.attachMedia(video)
          hls.on(HlsLib.Events.MEDIA_ATTACHED, () => hls.loadSource(src))
        } else {
          video.src = src
          video.load()
        }
      } else {
        video.src = src
        video.load()
      }
    }

    attach()
    return () => {
      cancelled = true
      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }
      video.removeAttribute('src')
      video.load()
    }
  }, [active, src, isHls, reloadKey])

  const attemptPlay = React.useCallback(
    async (reason: string, fromUserGesture = false) => {
      const video = ref.current
      if (!video) return
      const now = Date.now()
      if (now - playAttemptRef.current < 250) return
      playAttemptRef.current = now
      setPlaybackError(null)
      if (fromUserGesture) {
        // User gesture unlocks playback + optional unmute per browser policy.
        setNeedsUserGesture(false)
        if (hasUserGestureProp === undefined) {
          setLocalHasUserGesture(true)
        }
        hasUserGestureRef.current = true
        onUserGesture?.()
      }

      // iOS autoplay requires muted + playsInline; unmute only after a user gesture.
      const canPlayWithSound =
        soundEnabledRef.current && hasUserGestureRef.current
      const shouldMute = !fromUserGesture || !canPlayWithSound
      video.muted = shouldMute
      ;(video as any).playsInline = true
      video.setAttribute('playsinline', 'true')
      video.setAttribute('webkit-playsinline', 'true')

      try {
        const p = video.play()
        if (p && typeof p.then === 'function') {
          await p
        }
        if (canPlayWithSound && !video.paused) {
          // Unmute only when a user gesture has been recorded.
          video.muted = false
          video.volume = 1
        }
        setNeedsUserGesture(false)
      } catch (err) {
        if (!activeRef.current) return
        setNeedsUserGesture(true)
        if (isDev) {
          console.debug('[Clips] play() rejected', {
            reason,
            src,
            index,
            error: err,
            muted: video.muted,
            hasUserGesture: hasUserGestureRef.current,
            soundEnabled: soundEnabledRef.current,
          })
        }
      }
    },
    [hasUserGestureProp, index, onUserGesture, src],
  )

  React.useEffect(() => {
    if (!active) {
      setNeedsUserGesture(false)
      setIsBuffering(false)
      setIsPlaying(false)
      onPlayingChange?.(false)
      return
    }

    const video = ref.current
    if (!video) return
    attemptPlay('active')
  }, [active, attemptPlay, onPlayingChange, reloadKey, src])

  React.useEffect(() => {
    if (!active) return
    const video = ref.current
    if (!video) return
    const canPlayWithSound = soundEnabled && hasUserGesture
    // Never unmute without a prior user gesture (Chrome/Safari policy).
    const shouldMute = !canPlayWithSound || !isPlaying
    video.muted = shouldMute
    if (!shouldMute) {
      video.volume = 1
    }
  }, [active, soundEnabled, hasUserGesture, isPlaying])

  React.useEffect(() => {
    if (typeof document === 'undefined') return
    const video = ref.current
    if (!video) return
    const onHide = () => {
      video.pause()
      setIsPlaying(false)
      onPlayingChange?.(false)
    }
    document.addEventListener('visibilitychange', onHide)
    return () => {
      document.removeEventListener('visibilitychange', onHide)
    }
  }, [active, onPlayingChange])

  React.useEffect(() => {
    if (!active) return
    const video = ref.current
    if (!video) return

    const logVideo = (label: string) => {
      if (!isClipsDebug()) return
      console.debug(`[Clips][video] ${label}`, {
        src: video.currentSrc,
        readyState: video.readyState,
        networkState: video.networkState,
        paused: video.paused,
        muted: video.muted,
        volume: video.volume,
        errorCode: video.error?.code,
        errorMessage: video.error?.message,
      })
    }

    const onPlay = () => {
      setIsPlaying(true)
      setNeedsUserGesture(false)
      setIsBuffering(false)
      onPlayingChange?.(true)
      if (isDev) {
        console.debug('[Clips] play', {src, index})
      }
      logVideo('playing')
    }
    const onPause = () => {
      setIsPlaying(false)
      onPlayingChange?.(false)
      if (isDev) {
        console.debug('[Clips] pause', {src, index})
      }
      logVideo('pause')
    }
    const onWaiting = () => {
      setIsBuffering(true)
      logVideo('waiting')
    }
    const onCanPlay = () => {
      setIsBuffering(false)
      logVideo('canplay')
    }
    const onLoadedMetadata = () => logVideo('loadedmetadata')
    const onStalled = () => logVideo('stalled')
    const onError = () => {
      const error = video.error
      setPlaybackError('Impossible de lire cette video')
      setIsBuffering(false)
      if (isDev) {
        console.debug('[Clips] video error', {
          src,
          index,
          code: error?.code,
          message: error?.message,
        })
      }
      logVideo('error')
    }

    video.addEventListener('play', onPlay)
    video.addEventListener('pause', onPause)
    video.addEventListener('waiting', onWaiting)
    video.addEventListener('canplay', onCanPlay)
    video.addEventListener('loadedmetadata', onLoadedMetadata)
    video.addEventListener('stalled', onStalled)
    video.addEventListener('error', onError)
    return () => {
      video.removeEventListener('play', onPlay)
      video.removeEventListener('pause', onPause)
      video.removeEventListener('waiting', onWaiting)
      video.removeEventListener('canplay', onCanPlay)
      video.removeEventListener('loadedmetadata', onLoadedMetadata)
      video.removeEventListener('stalled', onStalled)
      video.removeEventListener('error', onError)
    }
  }, [active, index, onPlayingChange, src])

  React.useEffect(() => {
    if (!active) return
    const video = ref.current
    if (!video) return
    setFitClass('fitContain')

    const onLoadedMetadata = () => {
      const ratio = video.videoWidth / (video.videoHeight || 1)
      const isLandscape = ratio > 1.05
      const isTiktokPortrait = ratio < 0.75 && ratio > 0.45
      if (isLandscape) {
        setFitClass('fitContain')
      } else if (isTiktokPortrait) {
        setFitClass('fitCover')
      } else {
        setFitClass('fitContain')
      }
    }

    video.addEventListener('loadedmetadata', onLoadedMetadata)
    return () => {
      video.removeEventListener('loadedmetadata', onLoadedMetadata)
    }
  }, [active, src])

  const canPlayWithSound = soundEnabled && hasUserGesture
  const showSoundPrompt =
    active && isPlaying && !canPlayWithSound && (!soundPrefSet || soundEnabled)
  const showPlayOverlay =
    active && !playbackError && !isBuffering && (!isPlaying || needsUserGesture)

  return (
    <div
      className="reel-video-shell"
      onClick={() => {
        if (!active || playbackError) return
        if (needsUserGesture || !isPlaying) {
          attemptPlay('tap', true)
          return
        }
        if (canPlayWithSound) return
        if (!soundEnabledRef.current) {
          soundEnabledRef.current = true
          hasUserGestureRef.current = true
          if (soundEnabledProp === undefined) {
            setLocalSoundEnabled(true)
            setLocalSoundPrefSet(true)
            setLocalHasUserGesture(true)
          }
          onSoundChange?.(true, true)
          onUserGesture?.()
        } else {
          if (hasUserGestureProp === undefined) {
            setLocalHasUserGesture(true)
          }
          hasUserGestureRef.current = true
          onUserGesture?.()
        }
        const video = ref.current
        if (video && !video.paused) {
          video.muted = false
          video.volume = 1
        } else {
          attemptPlay('tap-unmute', true)
        }
      }}>
      {!active && poster && <img className="clip-poster" src={poster} alt="" />}
      {!active && !poster && <div className="clip-poster clip-poster--empty" />}
      {active && (
        <video
          ref={ref}
          className={[className, fitClass].filter(Boolean).join(' ')}
          poster={poster}
          muted={!canPlayWithSound || !isPlaying}
          playsInline
          autoPlay
          preload="metadata"
          loop
          controls={false}
          style={{
            width: '100%',
            height: '100%',
            background: '#000',
          }}
          crossOrigin="anonymous"
        />
      )}
      {showSoundPrompt && (
        <div className="clip-sound-prompt">
          <div className="clip-sound-pill">
            <span className="clip-sound-icon" aria-hidden>
              <SpeakerIcon size={18} fill="currentColor" />
            </span>
            <Text>Tap for sound</Text>
          </div>
        </div>
      )}
      {active && isBuffering && (
        <div className="clip-spinner" aria-live="polite">
          <Text>Chargement...</Text>
        </div>
      )}
      {active && playbackError && (
        <div className="clip-error">
          <div className="clip-error__text">
            <Text>{playbackError}</Text>
          </div>
          <button
            type="button"
            className="clip-error__retry"
            onClick={() => {
              setPlaybackError(null)
              setReloadKey(prev => prev + 1)
              attemptPlay('retry', true)
            }}>
            <Text>Reessayer</Text>
          </button>
        </div>
      )}
      {showPlayOverlay && (
        <div className="clip-tap">
          <button
            type="button"
            className="clip-tap__button"
            aria-label="Lire la vidéo"
            onClick={event => {
              event.stopPropagation()
              attemptPlay('tap', true)
            }}>
            <span className="clip-tap__icon" aria-hidden>
              <PlayIcon size={28} fill="currentColor" />
            </span>
          </button>
        </div>
      )}
    </div>
  )
}
